import { useState, useEffect } from 'react';
import { endorsementsApi } from '../services/endorsements';
import {
  EndorsementPublic,
  EndorsementWithProviders,
  EndorsementStatistics,
  EndorsementCreate,
} from '../types/endorsement';
import { ProviderPublic } from '../types/provider';
import { getApiErrorMessage } from '../utils/errors';

export const useEndorsementStatistics = () => {
  const [statistics, setStatistics] = useState<EndorsementStatistics>({
    endorsements_received: 0,
    endorsements_given: 0,
    endorsements_pending: 0,
    endorsed_by_top_scorers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        const data = await endorsementsApi.getStatistics();
        setStatistics(data);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Failed to fetch statistics'));
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  return { statistics, loading, error };
};

export const useReceivedEndorsements = (skip = 0, limit = 100) => {
  const [endorsements, setEndorsements] = useState<EndorsementPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEndorsements = async () => {
      try {
        setLoading(true);
        const data = await endorsementsApi.getReceived(skip, limit);
        setEndorsements(data);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Failed to fetch received endorsements'));
      } finally {
        setLoading(false);
      }
    };

    fetchEndorsements();
  }, [skip, limit]);

  return { endorsements, loading, error };
};

export const useSentEndorsements = (skip = 0, limit = 100) => {
  const [endorsements, setEndorsements] = useState<EndorsementWithProviders[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEndorsements = async () => {
      try {
        setLoading(true);
        const data = await endorsementsApi.getSent(skip, limit);
        setEndorsements(data);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Failed to fetch sent endorsements'));
      } finally {
        setLoading(false);
      }
    };

    fetchEndorsements();
  }, [skip, limit]);

  return { endorsements, loading, error };
};

export const usePendingEndorsements = (skip = 0, limit = 100) => {
  const [endorsements, setEndorsements] = useState<EndorsementPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEndorsements = async () => {
      try {
        setLoading(true);
        const data = await endorsementsApi.getPending(skip, limit);
        setEndorsements(data);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Failed to fetch pending endorsements'));
      } finally {
        setLoading(false);
      }
    };

    fetchEndorsements();
  }, [skip, limit]);

  return { endorsements, loading, error };
};

export const useProvidersToEndorse = (skip = 0, limit = 20) => {
  const [providers, setProviders] = useState<ProviderPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true);
        const data = await endorsementsApi.getProvidersToEndorse(skip, limit);
        setProviders(data);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Failed to fetch providers'));
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, [skip, limit]);

  return { providers, loading, error };
};

export const useEndorseProvider = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const endorseProvider = async (data: EndorsementCreate) => {
    try {
      setLoading(true);
      setError(null);
      const endorsement = await endorsementsApi.create(data);
      return endorsement;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to create endorsement');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { endorseProvider, loading, error };
};

export const useAcceptEndorsement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const acceptEndorsement = async (endorsementId: number) => {
    try {
      setLoading(true);
      setError(null);
      const result = await endorsementsApi.accept(endorsementId);
      return result;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to accept endorsement');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { acceptEndorsement, loading, error };
};

export const useRejectEndorsement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rejectEndorsement = async (endorsementId: number) => {
    try {
      setLoading(true);
      setError(null);
      const result = await endorsementsApi.reject(endorsementId);
      return result;
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Failed to reject endorsement');
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { rejectEndorsement, loading, error };
};
